import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { parse as parseQuery } from 'query-string';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import { getActiveCohorts } from '../api/Cohort';
import { CohortId } from '../api/Cohort/types/Cohort';
import { Founder } from '../api/founders';
import { Mentor, MentorId } from '../api/mentors';
import mentorsAPI from '../api/mentors';
import { Role } from '../api/user/Role';
import venturesAPI, { VentureDetails } from '../api/ventures';
import { Status, Venture, VentureId } from '../api/ventures/types/Venture';
import { SnackMessage } from '../components/common';
import { useAuth } from '../hooks';
import { fullName } from '../utils/String';
import { massAsyncRequest } from '../utils/api';
import { decodeQuery, isMobile } from '../utils/functions';
import { useResourceBundles } from './resource-bundles-context';
import { UserContext } from './user-context';

export interface VenturesContextProps {
  ventures: Venture[];
  mentor?: Mentor;
  cohorts: Array<{ label: string; value: CohortId | null }>;
  selectedVentureIds: Venture['id'][];
  loadedMentorsById: { [x: string]: Mentor[] };
  loadedFoundersById: { [x: string]: Founder[] };
  isMentorsAssigningVenture: { [x: string]: boolean };
  isFoundersAssigningVenture: { [x: string]: boolean };
  isSendingProfileUpdate: boolean;
  isChangingVentureStatus: boolean;
  mentorsAssignmentTargetVenture: Venture | null;
  foundersAssignmentTargetVenture: Venture | null;
  archiveTargetVentures: Venture[] | null;
  reportLinkTargetVentureIds: VentureId[] | null;
  profileUpdateTargetVentureIds: VentureId[] | null;
  schedulingWizardTargetVenture: Venture | null;
  showMentorSchedulingWarning: boolean;
  resetKey: number;
  setVentures: Dispatch<SetStateAction<Venture[]>>;
  setMentor: Dispatch<SetStateAction<Mentor | undefined>>;
  setCohorts: Dispatch<SetStateAction<Array<CohortOption>>>;
  setSelectedVentureIds: Dispatch<SetStateAction<Venture['id'][]>>;
  setLoadedMentorsById: Dispatch<SetStateAction<{ [x: string]: Mentor[] }>>;
  setLoadedFoundersById: Dispatch<SetStateAction<{ [x: string]: Founder[] }>>;
  setIsMentorsAssigningVenture: Dispatch<
    SetStateAction<{ [x: string]: boolean }>
  >;
  setIsFoundersAssigningVenture: Dispatch<
    SetStateAction<{ [x: string]: boolean }>
  >;
  setIsSendingProfileUpdate: Dispatch<SetStateAction<boolean>>;
  changeVentureStatus: (status: Status) => Promise<void>;
  handleVote: (id: VentureId) => void;
  sendVentureUpdate: (onClose: () => any) => Promise<void>;
  sendVentureUpdateProfiles: (onClose: () => {}) => Promise<void>;
  handleMentorsLoaded: (
    ventureId: Venture['id'],
  ) => (loadedMentors: Mentor[]) => void;
  handleFoundersLoaded: (
    ventureId: Venture['id'],
  ) => (loadedFounders: Founder[]) => void;
  assignMentorsSuccessForm: (
    ventureId: Venture['id'],
    handleModalClose: () => any,
  ) => (params: {
    assigned: string[];
    removed: string[];
    all: Mentor[];
  }) => any;
  assignFoundersSuccessForm: (
    ventureId: Venture['id'],
    handleModalClose: () => any,
  ) => (params: {
    assigned: string[];
    removed: string[];
    all: Founder[];
  }) => any;
  setMentorsAssignmentTargetVenture: Dispatch<SetStateAction<Venture | null>>;
  setFoundersAssignmentTargetVenture: Dispatch<SetStateAction<Venture | null>>;
  setArchiveTargetVentures: Dispatch<SetStateAction<Venture[] | null>>;
  setReportLinkTargetVentureIds: Dispatch<SetStateAction<VentureId[] | null>>;
  setProfileUpdateTargetVentureIds: Dispatch<
    SetStateAction<VentureId[] | null>
  >;
  setSchedulingWizardTargetVenture: Dispatch<SetStateAction<Venture | null>>;
  setShowMentorSchedulingWarning: Dispatch<SetStateAction<boolean>>;
  checkSchedulingWizardForMentor: (venture: Venture) => void;
  reset: () => void;
}

export const VenturesContext = createContext<VenturesContextProps>({
  ventures: [],
  mentor: undefined,
  cohorts: [],
  selectedVentureIds: [],
  loadedMentorsById: {},
  loadedFoundersById: {},
  isMentorsAssigningVenture: {},
  isFoundersAssigningVenture: {},
  isSendingProfileUpdate: false,
  isChangingVentureStatus: false,
  mentorsAssignmentTargetVenture: null,
  foundersAssignmentTargetVenture: null,
  archiveTargetVentures: null,
  reportLinkTargetVentureIds: null,
  profileUpdateTargetVentureIds: null,
  schedulingWizardTargetVenture: null,
  showMentorSchedulingWarning: false,
  resetKey: 0,
  setVentures: () => {},
  setMentor: () => {},
  setCohorts: () => {},
  setSelectedVentureIds: () => {},
  setLoadedMentorsById: () => {},
  setLoadedFoundersById: () => {},
  setIsMentorsAssigningVenture: () => {},
  setIsFoundersAssigningVenture: () => {},
  setIsSendingProfileUpdate: () => {},
  changeVentureStatus: async () => {},
  handleVote: () => {},
  sendVentureUpdate: async () => {},
  sendVentureUpdateProfiles: async () => {},
  handleMentorsLoaded: () => () => {},
  handleFoundersLoaded: () => () => {},
  assignMentorsSuccessForm: () => () => {},
  assignFoundersSuccessForm: () => () => {},
  setMentorsAssignmentTargetVenture: () => {},
  setFoundersAssignmentTargetVenture: () => {},
  setArchiveTargetVentures: () => {},
  setReportLinkTargetVentureIds: () => {},
  setProfileUpdateTargetVentureIds: () => {},
  setSchedulingWizardTargetVenture: () => {},
  checkSchedulingWizardForMentor: () => {},
  setShowMentorSchedulingWarning: () => {},
  reset: () => {},
});

interface VenturesProviderProps {
  children: ReactNode;
}

type CohortOption = { label: string; value: CohortId | null };

const validateVenture = (venture: Venture): VentureDetails => {
  return {
    status: venture.status,
    ventureName: venture.ventureName,
    url: venture.url,
    dateOfEnrollment: venture.dateOfEnrollment,
    dateOfCreation: venture.dateOfCreation,
    country: venture.country,
    state: venture.state,
    city: venture.city,
    zip: venture.zip,
    address: venture.address,
    numOfEmployees: venture.numOfEmployees,
    description: venture.description,
    isTech: venture.isTech,
    dateOfExit: venture.dateOfExit,
    exitReason: venture.exitReason,
    cohortId: venture.cohortId,
  };
};

export const VenturesProvider = ({ children }: VenturesProviderProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { rb } = useResourceBundles();
  const location = useLocation();

  const searchParams = useMemo(
    () => parseQuery(location.search),
    [location.search],
  );

  const { hasRole } = useContext(UserContext);
  const { tokenData } = useAuth();

  const isMentor = hasRole(Role.Mentor);

  const [ventures, setVentures] = useState<Venture[]>([]);
  const [mentor, setMentor] = useState<Mentor | undefined>(undefined);
  const [cohorts, setCohorts] = useState<Array<CohortOption>>([]);
  const [selectedVentureIds, setSelectedVentureIds] = useState<Venture['id'][]>(
    [],
  );
  const [loadedMentorsById, setLoadedMentorsById] = useState<{
    [x: string]: Mentor[];
  }>({});
  const [loadedFoundersById, setLoadedFoundersById] = useState<{
    [x: string]: Founder[];
  }>({});
  const [isMentorsAssigningVenture, setIsMentorsAssigningVenture] = useState<{
    [x: string]: boolean;
  }>({});
  const [isFoundersAssigningVenture, setIsFoundersAssigningVenture] = useState<{
    [x: string]: boolean;
  }>({});
  const [isSendingProfileUpdate, setIsSendingProfileUpdate] = useState(false);
  const [isChangingVentureStatus, setIsChangingVentureStatus] = useState(false);
  const [mentorsAssignmentTargetVenture, setMentorsAssignmentTargetVenture] =
    useState<Venture | null>(null);
  const [foundersAssignmentTargetVenture, setFoundersAssignmentTargetVenture] =
    useState<Venture | null>(null);
  const [reportLinkTargetVentureIds, setReportLinkTargetVentureIds] = useState<
    VentureId[] | null
  >(null);
  const [profileUpdateTargetVentureIds, setProfileUpdateTargetVentureIds] =
    useState<VentureId[] | null>(null);
  const [archiveTargetVentures, setArchiveTargetVentures] = useState<
    Venture[] | null
  >(null);
  const [schedulingWizardTargetVenture, setSchedulingWizardTargetVenture] =
    useState<Venture | null>(null);
  const [showMentorSchedulingWarning, setShowMentorSchedulingWarning] =
    useState(false);
  const [resetKey, setResetKey] = useState(0);

  const reset = useCallback(() => setResetKey((prev) => prev + 1), []);

  const handleVote = useCallback(
    (id: VentureId) => {
      if (!mentor) {
        return;
      }

      const venture = ventures.find((v) => v.id === id);

      if (!venture) return;

      if (
        venture.mentorsVoted?.includes(
          fullName(mentor.firstName, mentor.lastName),
        )
      ) {
        mentorsAPI.unvoteVenture(mentor.id, id).then(() =>
          setVentures((ventures) =>
            ventures.map((v) =>
              v.id === id
                ? {
                    ...v,
                    mentorsVoted:
                      v.mentorsVoted?.filter(
                        (m) =>
                          m !== fullName(mentor.firstName, mentor.lastName),
                      ) ?? null,
                  }
                : v,
            ),
          ),
        );
      } else {
        mentorsAPI.voteVenture(mentor.id, mentor.tenantId, id).then(() =>
          setVentures((ventures) =>
            ventures.map((v) =>
              v.id === id
                ? {
                    ...v,
                    mentorsVoted: [
                      ...(v.mentorsVoted ?? []),
                      fullName(mentor.firstName, mentor.lastName),
                    ],
                  }
                : v,
            ),
          ),
        );
      }
    },
    [mentor, ventures],
  );

  const sendVentureUpdate = useCallback(
    async (onClose: () => any) => {
      if (!profileUpdateTargetVentureIds) {
        return;
      }

      try {
        setIsSendingProfileUpdate(true);
        await venturesAPI.sendVentureUpdateInvites(
          profileUpdateTargetVentureIds,
        );
        enqueueSnackbar('The profile update request was successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [profileUpdateTargetVentureIds, enqueueSnackbar],
  );

  const sendVentureUpdateProfiles = useCallback(
    async (onClose: () => {}) => {
      try {
        setIsSendingProfileUpdate(true);
        await venturesAPI.sendVentureUpdateInvites(selectedVentureIds);
        enqueueSnackbar('The profile update requests were successfully sent', {
          variant: 'success',
        });
        setIsSendingProfileUpdate(false);
        onClose();
        setSelectedVentureIds([]);
      } catch (e) {
        const messageError = (e as AxiosError).response?.data?.message;

        setIsSendingProfileUpdate(false);
        enqueueSnackbar(
          'An error occurred while sending profile update request. Please, try again.',
          {
            content: (key, message) =>
              SnackMessage({
                key,
                message,
                variant: 'error',
                additionalMessage: messageError,
              }),
            variant: 'error',
          },
        );
        throw e;
      }
    },
    [enqueueSnackbar, selectedVentureIds],
  );

  const handleMentorsLoaded = useCallback(
    (ventureId: Venture['id']) => (loadedMentors: Mentor[]) => {
      setVentures((prevVentures) =>
        prevVentures.map((venture) =>
          venture.id === ventureId
            ? { ...venture, assignedMentors: loadedMentors.length }
            : venture,
        ),
      );
      setLoadedMentorsById((prevState) => ({
        ...prevState,
        [ventureId]: loadedMentors,
      }));
    },
    [],
  );

  const handleFoundersLoaded = useCallback(
    (ventureId: Venture['id']) => (loadedFounders: Founder[]) => {
      setVentures((prevVentures) =>
        prevVentures.map((venture) =>
          venture.id === ventureId
            ? { ...venture, assignedFounders: loadedFounders.length }
            : venture,
        ),
      );
      setLoadedFoundersById((prevState) => ({
        ...prevState,
        [ventureId]: loadedFounders,
      }));
    },
    [],
  );

  const assignMentorsSuccessForm = useCallback(
    (ventureId: Venture['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Mentor[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsMentorsAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: true,
          }));
          const assignedRequests = assigned.map(
            (mentorId) => () => venturesAPI.assignMentor(ventureId, mentorId),
          );
          const removedRequests = removed.map(
            (mentorId) => () =>
              venturesAPI.removeMentorAssignment(ventureId, mentorId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleMentorsLoaded(ventureId)(all);
          handleModalClose();
          setIsMentorsAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: false,
          }));
        } catch (e: any) {
          console.error('error', e);
        }
      },
    [handleMentorsLoaded],
  );

  const assignFoundersSuccessForm = useCallback(
    (ventureId: Venture['id'], handleModalClose: () => any) =>
      async ({
        assigned,
        removed,
        all,
      }: {
        assigned: string[];
        removed: string[];
        all: Founder[];
      }) => {
        if (!assigned.length && !removed.length) {
          return handleModalClose();
        }

        try {
          setIsFoundersAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: true,
          }));
          const assignedRequests = assigned.map(
            (founderId) => () =>
              venturesAPI.assignFounder(ventureId, founderId),
          );
          const removedRequests = removed.map(
            (founderId) => () =>
              venturesAPI.removeFounderAssignment(ventureId, founderId),
          );
          await Promise.all([
            massAsyncRequest(assignedRequests),
            massAsyncRequest(removedRequests),
          ]);
          handleFoundersLoaded(ventureId)(all);
          handleModalClose();
          setIsFoundersAssigningVenture((prev) => ({
            ...prev,
            [ventureId]: false,
          }));
        } catch (e: any) {
          const messageError = e.response?.data?.message;

          enqueueSnackbar(
            'An error occurred while assigning founders. Please, try again.',
            {
              content: (key, message) =>
                SnackMessage({
                  key,
                  message,
                  variant: 'error',
                  additionalMessage: messageError,
                }),
              variant: 'error',
            },
          );
        }
      },
    [handleFoundersLoaded, enqueueSnackbar],
  );

  const checkSchedulingWizardForMentor = useCallback(
    async (venture: Venture) => {
      if (isMentor) {
        let alreadyLoadedMentors = loadedMentorsById[venture.id];

        if (!alreadyLoadedMentors) {
          alreadyLoadedMentors = await mentorsAPI.getMentorsDetailsByVentureId(
            venture.id,
          );
          setLoadedMentorsById((prevState) => ({
            ...prevState,
            [venture.id]: alreadyLoadedMentors,
          }));
        }

        const isMentorAssigned = alreadyLoadedMentors.some(
          (m) => m.id === tokenData?.identityid,
        );

        if (!isMentorAssigned) {
          setShowMentorSchedulingWarning(true);
          return;
        }
      }

      setSchedulingWizardTargetVenture(venture);
    },
    [isMentor, loadedMentorsById, tokenData?.identityid],
  );

  const changeVentureStatus = useCallback(
    async (status: Status) => {
      try {
        if (!archiveTargetVentures?.length) return;
        setIsChangingVentureStatus(true);
        const changeStatus = archiveTargetVentures.map((value: Venture) => {
          return () =>
            venturesAPI.update(value.id, {
              ...validateVenture(value),
              status,
            });
        });
        const res = await massAsyncRequest(changeStatus);

        if (res.some((value) => value === undefined)) {
          throw new Error('An error occurred while changing venture status.');
        }
      } catch (e: any) {
        console.error(e);
        enqueueSnackbar(
          'An error occurred while changing venture status. Please, try again.',
          { variant: 'error' },
        );
      } finally {
        // reset to force reload
        if (isMobile()) {
          const decodedFilter = searchParams?.filter
            ? (decodeQuery(searchParams?.filter as string) as {
                type: string;
              })
            : null;

          if (!decodedFilter?.type || decodedFilter.type !== 'attention') {
            setVentures((prev) =>
              prev.filter((venture) =>
                archiveTargetVentures?.some((tf) => tf.id !== venture.id),
              ),
            );
          } else {
            setVentures((prev) =>
              prev.map((venture) =>
                archiveTargetVentures?.some((tf) => tf.id === venture.id)
                  ? { ...venture, status }
                  : venture,
              ),
            );
          }
        } else {
          reset();
        }

        setArchiveTargetVentures(null);
        setIsChangingVentureStatus(false);
      }
    },
    [archiveTargetVentures, enqueueSnackbar, reset, searchParams],
  );

  useEffect(() => {
    if (isMentor) {
      const loadMentor = async (id: MentorId) => {
        try {
          const loadedMentor = await mentorsAPI.getMentor(id);
          setMentor(loadedMentor);
        } catch (e: any) {
          if (e.response.status === 401) {
            console.error(e);
          } else {
            enqueueSnackbar(`Could not load ${rb('mentor')} details`, {
              variant: 'error',
            });
          }
        }
      };

      loadMentor(tokenData?.identityid as MentorId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMentor, tokenData?.identityid]);

  useEffect(() => {
    getActiveCohorts()
      .then((vs) =>
        vs.map((v) => ({
          value: v.id,
          label: v.name,
        })),
      )
      .then((vs) => setCohorts([{ label: 'All groups', value: null }, ...vs]))
      .catch(() =>
        enqueueSnackbar('Could not load groups details', { variant: 'error' }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contextValue: VenturesContextProps = {
    ventures,
    mentor,
    cohorts,
    selectedVentureIds,
    loadedMentorsById,
    loadedFoundersById,
    isMentorsAssigningVenture,
    isFoundersAssigningVenture,
    isSendingProfileUpdate,
    isChangingVentureStatus,
    mentorsAssignmentTargetVenture,
    foundersAssignmentTargetVenture,
    reportLinkTargetVentureIds,
    profileUpdateTargetVentureIds,
    archiveTargetVentures,
    schedulingWizardTargetVenture,
    showMentorSchedulingWarning,
    resetKey,
    setVentures,
    setMentor,
    setCohorts,
    setSelectedVentureIds,
    setLoadedMentorsById,
    setLoadedFoundersById,
    setIsMentorsAssigningVenture,
    setIsFoundersAssigningVenture,
    setIsSendingProfileUpdate,
    changeVentureStatus,
    handleVote,
    sendVentureUpdate,
    sendVentureUpdateProfiles,
    handleMentorsLoaded,
    handleFoundersLoaded,
    assignMentorsSuccessForm,
    assignFoundersSuccessForm,
    setMentorsAssignmentTargetVenture,
    setFoundersAssignmentTargetVenture,
    setReportLinkTargetVentureIds,
    setProfileUpdateTargetVentureIds,
    setArchiveTargetVentures,
    setSchedulingWizardTargetVenture,
    setShowMentorSchedulingWarning,
    checkSchedulingWizardForMentor,
    reset,
  };

  return (
    <VenturesContext.Provider value={contextValue}>
      {children}
    </VenturesContext.Provider>
  );
};

export const useVenturesContext = () => {
  const context = useContext(VenturesContext);
  if (!context) {
    throw new Error(
      'useVenturesContext must be used within a VenturesProvider',
    );
  }
  return context;
};
